import { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "@/i18n";
import { disablePosthog, enablePosthog } from "@/lib/posthog";
import { DEFAULT_PREFERENCES, persistCookieConsent, readCookieConsent } from "@/lib/cookieConsent";
import "@/components/cookie-consent.css";

const initialPreferences = DEFAULT_PREFERENCES;

const categories = [
  {
    id: "necessary",
    es: "Necesarias",
    en: "Necessary",
    descriptionEs: "Permiten navegar por el sitio y utilizar sus funciones básicas.",
    descriptionEn: "Allow you to browse the site and use its basic features.",
    required: true,
  },
  {
    id: "analytics",
    es: "Analíticas",
    en: "Analytics",
    descriptionEs: "Permiten analizar el uso del sitio para medir su actividad y mejorarlo.",
    descriptionEn: "Help analyze site usage so we can measure activity and improve it.",
  },
  {
    id: "advertising",
    es: "Publicitarias",
    en: "Advertising",
    descriptionEs: "Permiten gestionar los espacios publicitarios que pueda incluir el sitio.",
    descriptionEn: "Help manage advertising spaces that may appear on the site.",
  },
  {
    id: "social",
    es: "Sociales",
    en: "Social",
    descriptionEs: "Las establecen plataformas sociales para permitir compartir contenido.",
    descriptionEn: "Set by social platforms to enable content sharing.",
  },
  {
    id: "behavioralAds",
    es: "Publicidad comportamental",
    en: "Behavioral advertising",
    descriptionEs: "Recogen preferencias para mostrar publicidad basada en la navegación.",
    descriptionEn: "Collect preferences to show advertising based on browsing activity.",
  },
  {
    id: "affiliates",
    es: "Afiliados",
    en: "Affiliate",
    descriptionEs: "Permiten medir visitas que llegan desde webs afiliadas.",
    descriptionEn: "Help measure visits that come from affiliate websites.",
  },
  {
    id: "security",
    es: "Seguridad",
    en: "Security",
    descriptionEs: "Almacenan información para ayudar a proteger el sitio y sus datos.",
    descriptionEn: "Store information to help protect the site and its data.",
  },
];

const copy = {
  es: {
    title: "Este sitio web utiliza cookies",
    intro: "Utilizamos cookies propias y de terceros. Puedes aceptar todas, rechazar las no necesarias o configurar tus preferencias.",
    tabs: ["Consentir", "Detalles", "Acerca de"],
    accept: "Aceptar todas",
    reject: "Rechazar",
    save: "Guardar selección",
    aboutTitle: "¿Qué son las cookies?",
    about: "Las cookies y tecnologías similares permiten almacenar y recuperar información en tu navegador. Se utilizan, entre otros fines, para recordar preferencias y ayudar al correcto funcionamiento del sitio.",
    close: "Cerrar preferencias",
    open: "Configurar cookies",
    required: "Siempre activas",
  },
  en: {
    title: "This website uses cookies",
    intro: "We use first- and third-party cookies. You can accept all, reject non-essential cookies, or configure your preferences.",
    tabs: ["Consent", "Details", "About"],
    accept: "Accept all",
    reject: "Reject",
    save: "Save selection",
    aboutTitle: "What are cookies?",
    about: "Cookies and similar technologies store and retrieve information in your browser. They can be used to remember preferences and help the site work properly.",
    close: "Close cookie preferences",
    open: "Cookie settings",
    required: "Always active",
  },
};

export function openCookieSettings() {
  window.dispatchEvent(new Event("a4p:open-cookie-settings"));
}

export default function CookieConsent() {
  const { lang } = useLang();
  const text = copy[lang] || copy.es;
  const [preferences, setPreferences] = useState(() => readCookieConsent() || initialPreferences);
  const [open, setOpen] = useState(() => !readCookieConsent());
  const [tab, setTab] = useState(0);
  const [hasConsent, setHasConsent] = useState(() => Boolean(readCookieConsent()));

  const save = useCallback((next) => {
    const saved = { ...initialPreferences, ...next, necessary: true };
    persistCookieConsent(saved);
    setPreferences(saved);
    setHasConsent(true);
    setOpen(false);
    if (saved.analytics) enablePosthog();
    else disablePosthog();
  }, []);

  useEffect(() => {
    const handleOpen = () => {
      setPreferences(readCookieConsent() || initialPreferences);
      setTab(0);
      setOpen(true);
    };
    window.addEventListener("a4p:open-cookie-settings", handleOpen);
    return () => window.removeEventListener("a4p:open-cookie-settings", handleOpen);
  }, []);

  useEffect(() => {
    if (hasConsent && preferences.analytics) enablePosthog();
  }, [hasConsent, preferences.analytics]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const rows = useMemo(() => categories.map((category) => ({
    ...category,
    label: lang === "en" ? category.en : category.es,
    description: lang === "en" ? category.descriptionEn : category.descriptionEs,
  })), [lang]);

  if (!open) return null;

  return (
    <div className="cookie-overlay">
      <section className="cookie-panel" role="dialog" aria-modal="true" aria-labelledby="cookie-title" data-testid="cookie-consent-panel">
        <div className="cookie-panel__header">
          <div>
            <span className="cookie-panel__eyebrow">Analytics 4 People</span>
            <h2 id="cookie-title">{text.title}</h2>
          </div>
          {hasConsent && <button className="cookie-panel__close" onClick={() => setOpen(false)} aria-label={text.close}>×</button>}
        </div>
        <div className="cookie-tabs" role="tablist" aria-label={text.title}>
          {text.tabs.map((label, index) => (
            <button key={label} type="button" role="tab" aria-selected={tab === index} className={tab === index ? "is-active" : ""} onClick={() => setTab(index)}>{label}</button>
          ))}
        </div>
        <div className="cookie-panel__body">
          {tab === 0 && <p>{text.intro}</p>}
          {tab === 1 && <div className="cookie-category-list">
            {rows.map((category) => (
              <label className="cookie-category" key={category.id}>
                <span className="cookie-category__copy"><strong>{category.label}</strong><span>{category.description}</span></span>
                {category.required ? <span className="cookie-required">{text.required}</span> : (
                  <input type="checkbox" checked={Boolean(preferences[category.id])} onChange={(event) => setPreferences((current) => ({ ...current, [category.id]: event.target.checked }))} aria-label={category.label} />
                )}
              </label>
            ))}
          </div>}
          {tab === 2 && <div><h3>{text.aboutTitle}</h3><p>{text.about}</p></div>}
        </div>
        <div className="cookie-actions">
          <button type="button" className="cookie-button cookie-button--quiet" onClick={() => save(initialPreferences)}>{text.reject}</button>
          <button type="button" className="cookie-button cookie-button--outline" onClick={() => save(preferences)}>{text.save}</button>
          <button type="button" className="cookie-button cookie-button--primary" onClick={() => save(Object.fromEntries(Object.keys(initialPreferences).map((key) => [key, true])))}>{text.accept}</button>
        </div>
      </section>
    </div>
  );
}
